import { useEffect, useRef, useState } from 'react'
import { buyerNames, usCities } from '../data/buyers'
import { runWhenIdle } from '../utils/scheduling'
import { scanPageProducts } from '../utils/productScanner'
import type { PurchaseEvent, ScannedProduct } from '../types/purchase'

const FIRST_DELAY = 3000
const VISIBLE_MS = 4000
const MIN_GAP = 8000
const MAX_GAP = 15000
const NAME_HISTORY = 10

function randomItem<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)]
}

function pickProduct(catalog: ScannedProduct[]): ScannedProduct {
  const primary = catalog.filter(product => product.tier === 'primary')
  const secondary = catalog.filter(product => product.tier === 'secondary')
  if (primary.length > 0 && secondary.length > 0) {
    return Math.random() < 0.7 ? randomItem(primary) : randomItem(secondary)
  }
  return randomItem(catalog)
}

function pickName(history: string[]): string {
  const available = buyerNames.filter(name => !history.includes(name))
  return available.length > 0 ? randomItem(available) : randomItem(buyerNames)
}

interface PurchaseNotifications {
  current: PurchaseEvent | null
  catalogSize: number
}

/**
 * Scans the page for products, then emits one purchase toast at a time:
 * first after 3s, visible for 4s, repeating every 8–15s.
 */
export function usePurchaseNotifications(enabled: boolean): PurchaseNotifications {
  const [catalog, setCatalog] = useState<ScannedProduct[]>([])
  const [current, setCurrent] = useState<PurchaseEvent | null>(null)
  const catalogRef = useRef<ScannedProduct[]>([])
  const nameHistory = useRef<string[]>([])
  const timers = useRef<number[]>([])
  const counter = useRef(0)

  useEffect(() => {
    if (!enabled) return
    // The DOM scan is non-critical: run it once the page is loaded and idle.
    const handle = runWhenIdle(() => {
      const found = scanPageProducts(document)
      catalogRef.current = found
      setCatalog(found)
    })
    return handle.cancel
  }, [enabled])

  useEffect(() => {
    if (!enabled || catalog.length === 0) return

    function clearTimers() {
      timers.current.forEach(id => window.clearTimeout(id))
      timers.current = []
    }

    function scheduleNext(delay: number) {
      const id = window.setTimeout(show, delay)
      timers.current.push(id)
    }

    function show() {
      const products = catalogRef.current
      if (products.length === 0) return

      const name = pickName(nameHistory.current)
      nameHistory.current = [...nameHistory.current, name].slice(-NAME_HISTORY)
      counter.current += 1

      setCurrent({
        id: counter.current,
        name,
        city: randomItem(usCities),
        product: pickProduct(products)
      })

      const hideId = window.setTimeout(() => {
        setCurrent(null)
        scheduleNext(MIN_GAP + Math.random() * (MAX_GAP - MIN_GAP))
      }, VISIBLE_MS)
      timers.current.push(hideId)
    }

    scheduleNext(FIRST_DELAY)
    return clearTimers
  }, [enabled, catalog])

  return { current, catalogSize: catalog.length }
}
